import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  ArrowLeft,
  CurrencyEur,
  CheckCircle,
  Pencil,
  Trash,
  ArrowCounterClockwise,
  CopySimple,
  Table,
  SquaresFour as Cards,
} from "@phosphor-icons/react";
import BalanceDialog from "./BalanceDialog";
import BalancesTable from "./BalancesTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_METHODS = [
  "ΜΕΤΡΗΤΑ",
  "ΚΑΤΑΘΕΣΗ ΠΕΙΡΑΙΩΣ",
  "ΚΑΤΑΘΕΣΗ ALPHA",
  "ΑΛΛΟΣ ΤΡΟΠΟΣ",
];
const SOURCES = ["ΤΙΜΟΛΟΓΙΟ", "EMAIL", "ΤΗΛΕΦΩΝΟ"];
const PEOPLE = ["ΔΙΑΜΑΝΤΗΣ", "ΒΙΚΥ", "ΓΙΩΡΓΟΣ", "ΒΑΣΙΛΗΣ", "ΚΩΣΤΑΣ"];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("el-GR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString("el-GR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function BalancesView({ onExit }) {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending"); // pending | done
  const [pendingView, setPendingView] = useState("cards"); // cards | table
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchItems = async (which = tab) => {
    setLoading(true);
    try {
      const completed = which === "done";
      const resp = await axios.get(`${API}/balances?completed=${completed}`);
      setItems(resp.data || []);
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ ΦΟΡΤΩΣΗΣ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleSave = async (payload, id) => {
    try {
      if (id) {
        await axios.put(`${API}/balances/${id}`, payload);
        toast.success("ΑΠΟΘΗΚΕΥΤΗΚΕ");
      } else {
        await axios.post(`${API}/balances`, payload);
        toast.success("ΚΑΤΑΧΩΡΗΘΗΚΕ");
      }
      setDialogOpen(false);
      setEditing(null);
      fetchItems(tab);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "ΣΦΑΛΜΑ ΑΠΟΘΗΚΕΥΣΗΣ");
    }
  };

  const handleComplete = async (b) => {
    try {
      await axios.post(`${API}/balances/${b.id}/complete`);
      toast.success("ΕΞΟΦΛΗΘΗΚΕ");
      fetchItems(tab);
    } catch {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  const handleUncomplete = async (b) => {
    try {
      await axios.post(`${API}/balances/${b.id}/uncomplete`);
      toast.success("ΕΠΑΝΑΦΟΡΑ");
      fetchItems(tab);
    } catch {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`ΔΙΑΓΡΑΦΗ ΚΙΝΗΣΗΣ ΑΠΟ "${b.supplier}";`)) return;
    try {
      await axios.delete(`${API}/balances/${b.id}`);
      toast.success("ΔΙΑΓΡΑΦΗΚΕ");
      fetchItems(tab);
    } catch {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  const total = useMemo(
    () => items.reduce((s, x) => s + Number(x.amount || 0), 0),
    [items]
  );

  return (
    <div className="fixed inset-0 z-40 bg-stone-100 overflow-auto" data-testid="balances-view">
      <header className="sticky top-0 z-10 bg-stone-100/95 backdrop-blur border-b border-black/5 px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={onExit}
            data-testid="balances-back-btn"
            className="px-4 py-2.5 rounded-full flex items-center gap-2 text-sm font-bold uppercase tracking-wider border-2 bg-white/70 text-ink border-ink/15 hover:border-ink/40 active:scale-95"
          >
            <ArrowLeft size={16} weight="bold" />
            ΠΙΣΩ
          </button>
          <h2 className="font-caveat text-5xl text-ink leading-none flex items-center gap-2">
            <CurrencyEur size={32} weight="bold" className="text-orange-600" />
            ΥΠΟΛΟΙΠΑ
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setTab("pending")}
            data-testid="balances-tab-pending"
            className={`px-5 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider border-2 transition-all ${
              tab === "pending"
                ? "bg-orange-500 text-white border-orange-600 shadow"
                : "bg-white/70 text-ink/70 border-ink/15 hover:border-orange-400"
            }`}
          >
            ΕΚΚΡΕΜΟΥΝ
          </button>
          <button
            onClick={() => setTab("done")}
            data-testid="balances-tab-done"
            className={`px-5 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider border-2 transition-all ${
              tab === "done"
                ? "bg-emerald-600 text-white border-emerald-700 shadow"
                : "bg-white/70 text-ink/70 border-ink/15 hover:border-emerald-400"
            }`}
          >
            ΕΞΟΦΛΗΜΕΝΑ
          </button>

          {tab === "pending" && (
            <button
              onClick={() => setPendingView((v) => (v === "cards" ? "table" : "cards"))}
              data-testid="balances-view-toggle"
              title={pendingView === "cards" ? "ΠΡΟΒΟΛΗ ΣΕ ΠΙΝΑΚΑ" : "ΠΡΟΒΟΛΗ ΣΕ POSTIT"}
              className="ml-2 px-4 py-2.5 rounded-full flex items-center gap-2 text-sm font-bold uppercase tracking-wider border-2 bg-white/70 text-ink border-ink/15 hover:border-ink/40 active:scale-95"
            >
              {pendingView === "cards" ? (
                <>
                  <Table size={16} weight="bold" />
                  ΠΙΝΑΚΑΣ
                </>
              ) : (
                <>
                  <Cards size={16} weight="bold" />
                  POSTIT
                </>
              )}
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div
            data-testid="balances-total"
            className="px-4 py-2.5 rounded-full bg-white/80 border-2 border-ink/15 text-sm font-bold uppercase tracking-wider text-ink"
          >
            ΣΥΝΟΛΟ&nbsp;
            <span className="text-orange-700 text-base">{fmtMoney(total)} €</span>
          </div>
          <button
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            data-testid="balances-new-btn"
            className="px-5 py-2.5 rounded-full flex items-center gap-2 text-sm font-bold uppercase tracking-wider border-2 bg-orange-500 text-white border-orange-600 hover:bg-orange-600 active:scale-95 shadow"
          >
            <Plus size={16} weight="bold" />
            ΝΕΑ ΚΑΤΑΧΩΡΗΣΗ
          </button>
        </div>
      </header>

      <main className="px-6 py-6">
        {loading ? (
          <p className="text-center text-ink-muted font-caveat text-4xl">ΦΟΡΤΩΣΗ...</p>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-caveat text-5xl text-ink/60">
              {tab === "pending" ? "ΔΕΝ ΥΠΑΡΧΟΥΝ ΕΚΚΡΕΜΗ ΥΠΟΛΟΙΠΑ" : "ΔΕΝ ΥΠΑΡΧΟΥΝ ΕΞΟΦΛΗΜΕΝΑ"}
            </p>
          </div>
        ) : tab === "done" ? (
          <BalancesTable
            items={items}
            actions={(b) => (
              <>
                <button
                  onClick={() => handleUncomplete(b)}
                  data-testid={`balance-uncomplete-${b.id}`}
                  title="ΕΠΑΝΑΦΟΡΑ ΣΕ ΕΚΚΡΕΜΗ"
                  className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-orange-600 bg-white text-orange-700 hover:bg-orange-50 active:scale-95 inline-flex items-center gap-1"
                >
                  <ArrowCounterClockwise size={13} weight="bold" />
                  ΕΠΑΝΑΦ.
                </button>
                <button
                  onClick={() => handleDelete(b)}
                  data-testid={`balance-delete-${b.id}`}
                  title="ΔΙΑΓΡΑΦΗ"
                  className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-red-300 bg-white text-red-700 hover:bg-red-50 active:scale-95 inline-flex items-center gap-1"
                >
                  <Trash size={13} weight="bold" />
                  ΔΙΑΓΡ.
                </button>
              </>
            )}
          />
        ) : pendingView === "table" ? (
          <BalancesTable
            items={items}
            actions={(b) => (
              <>
                <button
                  onClick={() => handleComplete(b)}
                  data-testid={`balance-complete-${b.id}`}
                  title="ΕΚΤΕΛΕΣΜΕΝΟ"
                  className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-emerald-700 bg-white text-emerald-700 hover:bg-emerald-50 active:scale-95 inline-flex items-center gap-1"
                >
                  <CheckCircle size={13} weight="bold" />
                  ΕΚΤΕΛ.
                </button>
                <button
                  onClick={() => {
                    setEditing(b);
                    setDialogOpen(true);
                  }}
                  data-testid={`balance-edit-${b.id}`}
                  title="ΔΙΟΡΘΩΣΗ"
                  className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-ink/30 bg-white text-ink hover:bg-stone-50 active:scale-95 inline-flex items-center gap-1"
                >
                  <Pencil size={13} weight="bold" />
                  ΔΙΟΡΘ.
                </button>
                <button
                  onClick={() => {
                    setEditing({
                      supplier: b.supplier,
                      entry_date: b.entry_date,
                    });
                    setDialogOpen(true);
                  }}
                  data-testid={`balance-duplicate-${b.id}`}
                  title="ΑΝΤΙΓΡΑΦΟ"
                  className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-sky-600 bg-white text-sky-700 hover:bg-sky-50 active:scale-95 inline-flex items-center gap-1"
                >
                  <CopySimple size={13} weight="bold" />
                  ΑΝΤΙΓΡ.
                </button>
                <button
                  onClick={() => handleDelete(b)}
                  data-testid={`balance-delete-${b.id}`}
                  title="ΔΙΑΓΡΑΦΗ"
                  className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-red-300 bg-white text-red-700 hover:bg-red-50 active:scale-95 inline-flex items-center gap-1"
                >
                  <Trash size={13} weight="bold" />
                  ΔΙΑΓΡ.
                </button>
              </>
            )}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((b) => (
              <BalanceCard
                key={b.id}
                b={b}
                onEdit={() => {
                  setEditing(b);
                  setDialogOpen(true);
                }}
                onDuplicate={() => {
                  // Open a NEW-balance dialog pre-seeded with supplier +
                  // entry_date so the user can fill the rest. No id so save
                  // creates a fresh record (the original stays intact).
                  setEditing({
                    supplier: b.supplier,
                    entry_date: b.entry_date,
                  });
                  setDialogOpen(true);
                }}
                onComplete={() => handleComplete(b)}
                onUncomplete={() => handleUncomplete(b)}
                onDelete={() => handleDelete(b)}
                isDone={tab === "done"}
              />
            ))}
          </div>
        )}
      </main>

      <BalanceDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setEditing(null);
        }}
        initial={editing}
        onSave={handleSave}
        paymentMethods={PAYMENT_METHODS}
        sources={SOURCES}
        people={PEOPLE}
      />
    </div>
  );
}

function BalanceCard({ b, onEdit, onDuplicate, onComplete, onUncomplete, onDelete, isDone }) {
  return (
    <div
      data-testid={`balance-card-${b.id}`}
      className="relative rounded-md p-4 shadow-md border border-orange-700/30"
      style={{
        background: isDone
          ? "linear-gradient(180deg, #fde68a 0%, #fcd34d 100%)"
          : "linear-gradient(180deg, #fdba74 0%, #fb923c 100%)",
        opacity: isDone ? 0.85 : 1,
      }}
    >
      {/* Tape strip */}
      <span
        className="absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-3 bg-amber-200/80 rounded-sm shadow-sm rotate-[-2deg]"
        aria-hidden
      />

      {isDone && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden rounded-md">
          <div className="-rotate-[14deg] border-4 border-emerald-700/55 text-emerald-700/65 font-black text-xl tracking-widest px-5 py-1.5 rounded-md">
            ΕΞΟΦΛΗΘΗΚΕ
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="font-bold text-ink uppercase tracking-wide text-xl leading-tight">
          {b.supplier}
        </div>
        <div className="text-xs uppercase tracking-widest text-ink/55 font-bold whitespace-nowrap">
          {formatDate(b.entry_date)}
        </div>
      </div>

      <div className="text-3xl font-black text-ink mb-3" data-testid={`balance-amount-${b.id}`}>
        {fmtMoney(b.amount)} €
      </div>

      <div className="grid grid-cols-1 gap-1 text-sm uppercase tracking-wide font-semibold text-ink/85 mb-3">
        <div>
          <span className="text-ink/55">ΤΡΟΠΟΣ:</span> {b.payment_method}
        </div>
        <div>
          <span className="text-ink/55">ΠΗΓΗ:</span> {b.source}
        </div>
        <div>
          <span className="text-ink/55">ΑΠΟ:</span> {b.created_by}
        </div>
        {b.execution_date ? (
          <div>
            <span className="text-ink/55">ΕΚΤΕΛΕΣΗ:</span> {formatDate(b.execution_date)}
          </div>
        ) : null}
      </div>

      {b.comment ? (
        <div className="text-sm text-ink/85 italic mb-3 whitespace-pre-wrap break-words border-t border-ink/10 pt-2">
          {b.comment}
        </div>
      ) : null}

      <div className="flex items-center gap-1.5 flex-wrap pt-2 border-t border-ink/10">
        {isDone ? (
          <button
            onClick={onUncomplete}
            data-testid={`balance-uncomplete-${b.id}`}
            title="ΕΠΑΝΑΦΟΡΑ ΣΕ ΕΚΚΡΕΜΗ"
            className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-orange-600 bg-white/70 text-orange-700 hover:bg-orange-50 active:scale-95 flex items-center gap-1"
          >
            <ArrowCounterClockwise size={14} weight="bold" />
            ΕΠΑΝΑΦΟΡΑ
          </button>
        ) : (
          <button
            onClick={onComplete}
            data-testid={`balance-complete-${b.id}`}
            title="ΕΚΤΕΛΕΣΜΕΝΟ - ΜΕΤΑΦΟΡΑ ΣΤΑ ΕΞΟΦΛΗΜΕΝΑ"
            className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-emerald-700 bg-white/70 text-emerald-700 hover:bg-emerald-50 active:scale-95 flex items-center gap-1"
          >
            <CheckCircle size={14} weight="bold" />
            ΕΚΤΕΛΕΣΜΕΝΟ
          </button>
        )}
        <button
          onClick={onEdit}
          data-testid={`balance-edit-${b.id}`}
          title="ΔΙΟΡΘΩΣΗ"
          className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-ink/30 bg-white/70 text-ink hover:bg-white active:scale-95 flex items-center gap-1"
        >
          <Pencil size={14} weight="bold" />
          ΔΙΟΡΘΩΣΗ
        </button>
        {!isDone && (
          <button
            onClick={onDuplicate}
            data-testid={`balance-duplicate-${b.id}`}
            title="ΑΝΤΙΓΡΑΦΟ ΜΕ ΙΔΙΟ ΠΡΟΜΗΘΕΥΤΗ & ΗΜΕΡΟΜΗΝΙΑ"
            className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-sky-600 bg-white/70 text-sky-700 hover:bg-sky-50 active:scale-95 flex items-center gap-1"
          >
            <CopySimple size={14} weight="bold" />
            ΑΝΤΙΓΡΑΦΟ
          </button>
        )}
        <button
          onClick={onDelete}
          data-testid={`balance-delete-${b.id}`}
          title="ΔΙΑΓΡΑΦΗ"
          className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-red-300 bg-white/70 text-red-700 hover:bg-red-50 active:scale-95 flex items-center gap-1"
        >
          <Trash size={14} weight="bold" />
          ΔΙΑΓΡ.
        </button>
      </div>
    </div>
  );
}
